/**
 * Low-level Python source scanners (delimiters, strings, multiline calls, top-level splits).
 *
 * Layering in this repo:
 * - **This module** — lexical / structural scans with no product domain knowledge.
 * - **`hardwareParser.ts`** — orchestrates LSP + regex to build hardware/editor state from code.
 * - **`driveModeAutonomousActionUtils.ts`** — action labels, expressions, program-start calls.
 * - **`sensorCodeUtils.ts`** — sensor mapping regions and trigger chains.
 * - **`sensor-stream-tracker.ts`** — dashboard `set_request_list` sync.
 */

export interface ExtractedCallArgsText {
  argsText: string;
  endLineIndex: number;
}

export interface BalancedDelimiterSpan {
  inner: string;
  closeIndex: number;
}

export interface PythonCallLineRange {
  startLineIndex: number;
  endLineIndex: number;
}

export interface PythonDelimiterScanOptions {
  ignoreLineComments?: boolean;
}

export function splitCodeLines(code: string): string[] {
  return code.split(/\r?\n/);
}

export function skipWhitespaceInCode(code: string, index: number): number {
  while (index < code.length && /\s/.test(code[index] ?? '')) index++;
  return index;
}

/** 0-based byte offset of `column` on `lineIndex` in a line array joined with `\n`. */
export function offsetAtLineColumn(lines: string[], lineIndex: number, column: number): number {
  let offset = 0;
  for (let i = 0; i < lineIndex; i++) {
    offset += (lines[i]?.length ?? 0) + 1;
  }
  return offset + column;
}

function offsetAtCodeLineColumn(code: string, lineIndex: number, column: number): number {
  let offset = 0;
  for (let i = 0; i < lineIndex && offset < code.length; i++) {
    const nextLineIndex = code.indexOf('\n', offset);
    if (nextLineIndex < 0) return code.length;
    offset = nextLineIndex + 1;
  }
  return offset + column;
}

/** Leading whitespace on the line containing `index`. */
export function getIndentAtCodeIndex(code: string, index: number): string {
  const lineStart = code.lastIndexOf('\n', index - 1) + 1;
  const match = code.slice(lineStart, index).match(/^\s*/);
  return match?.[0] ?? '';
}

/** Extends `index` through trailing spaces/tabs and one line break. */
export function consumeThroughLineBreak(code: string, index: number): number {
  let end = index;
  while (end < code.length && (code[end] === ' ' || code[end] === '\t')) end++;
  if (code[end] === '\r') end++;
  if (code[end] === '\n') end++;
  return end;
}

type PythonQuote = '"' | "'";

interface PythonStringScanState {
  quote: PythonQuote | null;
  triple: boolean;
  raw: boolean;
  escaped: boolean;
}

function createPythonStringScanState(): PythonStringScanState {
  return { quote: null, triple: false, raw: false, escaped: false };
}

function resetPythonStringScanState(state: PythonStringScanState) {
  state.quote = null;
  state.triple = false;
  state.raw = false;
  state.escaped = false;
}

const PYTHON_STRING_PREFIXES = new Set(['r', 'u', 'b', 'f', 'br', 'rb', 'fr', 'rf']);

function isPythonIdentifierChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

function findPythonStringLiteralStart(code: string, index: number): { quote: PythonQuote; triple: boolean; raw: boolean; consumed: number } | null {
  const ch = code[index] ?? '';
  if (ch === '"' || ch === "'") {
    const quote = ch as PythonQuote;
    const triple = code.startsWith(quote.repeat(3), index);
    return { quote, triple, raw: false, consumed: triple ? 3 : 1 };
  }

  if (!/[rRuUbBfF]/.test(ch)) return null;
  const prev = code[index - 1] ?? '';
  if (prev && isPythonIdentifierChar(prev)) return null;

  for (const prefixLength of [2, 1]) {
    const prefix = code.slice(index, index + prefixLength);
    if (!PYTHON_STRING_PREFIXES.has(prefix.toLowerCase())) continue;
    const quoteChar = code[index + prefixLength] ?? '';
    if (quoteChar !== '"' && quoteChar !== "'") continue;
    const quote = quoteChar as PythonQuote;
    const quoteIndex = index + prefixLength;
    const triple = code.startsWith(quote.repeat(3), quoteIndex);
    return {
      quote,
      triple,
      raw: prefix.toLowerCase().includes('r'),
      consumed: prefixLength + (triple ? 3 : 1),
    };
  }

  return null;
}

function hasOddBackslashRunBefore(code: string, index: number): boolean {
  let count = 0;
  for (let i = index - 1; i >= 0 && code[i] === '\\'; i--) {
    count++;
  }
  return count % 2 === 1;
}

function scanPythonStringLiteralAt(state: PythonStringScanState, code: string, index: number): number {
  const ch = code[index] ?? '';
  if (state.quote) {
    if (state.raw && !state.triple) {
      if (ch === state.quote && !hasOddBackslashRunBefore(code, index)) {
        resetPythonStringScanState(state);
      }
    } else if (state.escaped) {
      state.escaped = false;
    } else if ((!state.raw || code[index + 1] === state.quote) && ch === '\\') {
      state.escaped = true;
    } else if (state.triple) {
      if (code.startsWith(state.quote.repeat(3), index)) {
        resetPythonStringScanState(state);
        return 3;
      }
    } else if (ch === state.quote) {
      resetPythonStringScanState(state);
    }
    return 1;
  }

  const start = findPythonStringLiteralStart(code, index);
  if (start) {
    state.quote = start.quote;
    state.triple = start.triple;
    state.raw = start.raw;
    state.escaped = false;
    return start.consumed;
  }

  return 0;
}

export function isPlainPythonQuotedStringLiteral(value: string): boolean {
  return (
    ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) &&
    !value.startsWith('"""') &&
    !value.startsWith("'''")
  );
}

/**
 * Returns the index of the closing delimiter matching `openChar` at `openIndex`, or -1.
 * Respects single- and double-quoted Python string literals. When `ignoreLineComments`
 * is true, delimiters after `#` outside a string are ignored through the line end.
 */
export function findMatchingDelimiterIndex(
  code: string,
  openIndex: number,
  openChar: string,
  closeChar: string,
  options: PythonDelimiterScanOptions = {}
): number {
  let depth = 0;
  const stringState = createPythonStringScanState();

  for (let i = openIndex; i < code.length; i++) {
    const ch = code[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }
    if (options.ignoreLineComments && ch === '#') {
      while (i < code.length && code[i] !== '\n' && code[i] !== '\r') i++;
      continue;
    }

    if (ch === openChar) depth++;
    else if (ch === closeChar) {
      depth--;
      if (depth === 0) return i;
    }
  }

  return -1;
}

/** Inner text and closing index for a balanced delimiter pair starting at `openIndex`. */
export function readBalancedDelimiterSpan(
  text: string,
  openIndex: number,
  openChar: string,
  closeChar: string,
  options: PythonDelimiterScanOptions = {}
): BalancedDelimiterSpan | null {
  if (text[openIndex] !== openChar) return null;
  const closeIndex = findMatchingDelimiterIndex(text, openIndex, openChar, closeChar, options);
  if (closeIndex === -1) return null;
  return { inner: text.slice(openIndex + 1, closeIndex), closeIndex };
}

export function findBalancedCallLineRanges(
  lines: string[],
  matchesCallLine: (line: string, lineIndex: number) => boolean,
  getOpenParenIndex: (line: string) => number | null
): PythonCallLineRange[] {
  const ranges: PythonCallLineRange[] = [];
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? '';
    if (!matchesCallLine(line, lineIndex)) {
      lineIndex++;
      continue;
    }

    const openParenIndex = getOpenParenIndex(line);
    if (openParenIndex === null || openParenIndex < 0) {
      lineIndex++;
      continue;
    }

    let depth = 1;
    const stringState = createPythonStringScanState();
    let currentLineIndex = lineIndex;
    let currentColumn = openParenIndex + 1;

    while (currentLineIndex < lines.length && depth > 0) {
      const content = lines[currentLineIndex] ?? '';
      for (let i = currentColumn; i < content.length; i++) {
        const ch = content[i];
        const stringChars = scanPythonStringLiteralAt(stringState, content, i);
        if (stringChars > 0) {
          i += stringChars - 1;
          continue;
        }

        if (ch === '#') break;

        if (ch === '(') depth++;
        else if (ch === ')') {
          depth--;
          if (depth === 0) {
            ranges.push({ startLineIndex: lineIndex, endLineIndex: currentLineIndex });
            lineIndex = currentLineIndex;
            break;
          }
        }
      }
      currentLineIndex++;
      currentColumn = 0;
    }

    lineIndex++;
  }

  return ranges;
}

export function findFirstUnmatchedOpenParenIndex(line: string): number | null {
  const stack: number[] = [];
  const stringState = createPythonStringScanState();

  for (let i = 0; i < line.length; i++) {
    const ch = line[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, line, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }

    if (ch === '#') break;
    if (ch === '(') stack.push(i);
    else if (ch === ')' && stack.length > 0) stack.pop();
  }

  return stack[0] ?? null;
}

/**
 * Splits on commas at top-level delimiter depth, respecting quoted strings and backslash escapes.
 */
export function splitTopLevelByComma(value: string): string[] {
  const items: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  for (let i = 0; i < value.length; i++) {
    const ch = value[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, value, i);
    if (stringChars > 0) {
      current += value.slice(i, i + stringChars);
      i += stringChars - 1;
      continue;
    }

    // Track all bracket kinds so commas inside tuples, lists, or dicts
    // (e.g. `lines=[(1, "Ready"), (2, "Go")]`) are not treated as top-level separators.
    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === ',' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      items.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }
  if (current.trim()) items.push(current.trim());
  return items;
}

/**
 * Returns the index of a top-level Python keyword-assignment `=` operator, or -1.
 * Ignores string literals, nested delimiters, and comparison/walrus operators.
 */
export function findTopLevelKeywordAssignmentIndex(value: string): number {
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  for (let i = 0; i < value.length; i++) {
    const ch = value[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, value, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }

    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === '=' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      const prev = value[i - 1] ?? '';
      const next = value[i + 1] ?? '';
      if (prev !== '=' && prev !== '!' && prev !== '<' && prev !== '>' && prev !== ':' && next !== '=') return i;
    }
  }

  return -1;
}

type PythonOutsideStringCharAction = 'continue' | 'stop' | 'skip-rest-of-line';

function scanPythonCodeRespectingStrings(
  code: string,
  handlers: {
    onString?: (slice: string) => void;
    onChar?: (ch: string) => PythonOutsideStringCharAction;
  }
): void {
  const stringState = createPythonStringScanState();

  for (let i = 0; i < code.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      handlers.onString?.(code.slice(i, i + stringChars));
      i += stringChars - 1;
      continue;
    }

    const ch = code[i] ?? '';
    const action = handlers.onChar?.(ch) ?? 'continue';
    if (action === 'stop') return;
    if (action === 'skip-rest-of-line') {
      while (i < code.length && code[i] !== '\n' && code[i] !== '\r') i++;
    }
  }
}

function stripTrailingPythonLineComment(value: string): string {
  return stripPythonLineCommentsOutsideStrings(value).trim();
}

function pythonLineIndentColumn(line: string): number {
  return (line.match(/^\s*/)?.[0] ?? '').replace(/\t/g, '    ').length;
}

function isOffsetInPythonStringOrLineComment(code: string, offset: number): boolean {
  const stringState = createPythonStringScanState();
  for (let i = 0; i < Math.min(offset, code.length); i++) {
    const ch = code[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      if (i + stringChars > offset) return true;
      i += stringChars - 1;
      continue;
    }
    if (ch === '#') {
      while (i < code.length && i < offset && code[i] !== '\n' && code[i] !== '\r') i++;
      if (i >= offset) return true;
    }
  }
  return Boolean(stringState.quote);
}

/** Parses double-quoted Python string literals from a list/tuple inner fragment. */
export function parseDoubleQuotedStringLiterals(inner: string): string[] {
  const keys: string[] = [];
  const re = /"((?:[^"\\]|\\.)*)"/g;
  let match: RegExpExecArray | null = null;
  while ((match = re.exec(inner))) {
    keys.push(unescapePythonDoubleQuotedLiteral(match[1] ?? ''));
  }
  return keys;
}

/** True when `code` contains a Python `#` line comment outside quoted string literals. */
export function containsPythonLineCommentOutsideStrings(code: string): boolean {
  let found = false;

  scanPythonCodeRespectingStrings(code, {
    onChar: (ch) => {
      if (ch === '#') {
        found = true;
        return 'stop';
      }
      return 'continue';
    },
  });

  return found;
}

/** Removes Python `#` line comments outside quoted string literals. Newlines are preserved. */
export function stripPythonLineCommentsOutsideStrings(code: string): string {
  let out = '';

  scanPythonCodeRespectingStrings(code, {
    onString: (slice) => {
      out += slice;
    },
    onChar: (ch) => {
      if (ch === '#') return 'skip-rest-of-line';
      out += ch;
      return 'continue';
    },
  });

  return out;
}

function unescapePythonDoubleQuotedLiteral(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}

/**
 * Returns the unescaped string value of a plain (non-triple-quoted) Python string literal.
 * Strips the outer quote characters and unescapes `\\` and the relevant quote escape sequence.
 * Accepts values already validated by `isPlainPythonQuotedStringLiteral`.
 */
export function parsePythonPlainStringLiteralValue(literal: string): string {
  const quote = literal[0] as '"' | "'";
  const inner = literal.slice(1, -1);
  if (quote === '"') {
    return inner.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return inner.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
}

/**
 * Extracts the inner text of a balanced `(...)` call that may span multiple lines.
 * Whitespace between lines is normalized to a single space (matches editor parser behavior).
 * When `ignoreLineComments` is true, `#` comments are skipped through the line end.
 */
export function extractCallArgsText(
  lines: string[],
  startLineIndex: number,
  openParenIndex: number,
  options: PythonDelimiterScanOptions = {}
): ExtractedCallArgsText | null {
  let depth = 1;
  const stringState = createPythonStringScanState();
  let curLineIndex = startLineIndex;
  let curCharIndex = openParenIndex + 1;
  const chunks: string[] = [];

  while (curLineIndex < lines.length && depth > 0) {
    const content = lines[curLineIndex] ?? '';
    let hitLineComment = false;
    for (let i = curCharIndex; i < content.length; i++) {
      const ch = content[i];
      const stringChars = scanPythonStringLiteralAt(stringState, content, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (options.ignoreLineComments && ch === '#') {
        hitLineComment = true;
        break;
      }

      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) {
          chunks.push(content.slice(curCharIndex, i));
          const argsText = chunks
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
          return { argsText, endLineIndex: curLineIndex };
        }
      }
    }
    if (depth > 0 && !hitLineComment) {
      const segment = curLineIndex === startLineIndex ? content.slice(openParenIndex + 1) : content;
      if (segment.trim().length) chunks.push(segment.trim());
    }
    curLineIndex++;
    curCharIndex = 0;
  }

  return null;
}

/** Matches constructor assignments like `varName = ClassName(`, including optional type annotations and qualified class names. */
export const VAR_ASSIGN_RE = /^\s*([a-zA-Z_][\w]*)\s*(?::\s*[^=]+?)?=\s*(?:[A-Za-z_][\w]*\.)*([A-Za-z_][\w]*)\s*\(/;

export type FindVarAssignmentLineIndexOpts = {
  /** When set, only matches declarations whose constructor class is in this set. */
  allowedClassNames?: ReadonlySet<string>;
  /** `last` (default) keeps the latest allowed constructor; `first` keeps the earliest unless later rebound. */
  selection?: 'first' | 'last';
  /** `clear` (default) drops the match on a later disallowed constructor; `skip` keeps the prior allowed match. */
  disallowedConstructor?: 'clear' | 'skip';
};

export type TopLevelVarAssignment = {
  lineIndex: number;
  variableName: string;
  className: string;
};

export function leadingWhitespaceLength(line: string): number {
  const match = /^(\s*)/.exec(line);
  return match ? match[1].length : 0;
}

/** True when `line` is executable top-level program code (not blank or a comment). */
export function isTopLevelCodeLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return false;
  return leadingWhitespaceLength(line) === 0;
}

/** Matches top-level assignments like `varName = ...`, including non-constructor right-hand sides. */
const TOP_LEVEL_VAR_ASSIGN_RE = /^\s*([a-zA-Z_][\w]*)\s*(?::\s*[^=]+?)?=/;

type TopLevelVarAssignmentEvent =
  | { kind: 'constructor'; lineIndex: number; variableName: string; className: string }
  | { kind: 'rebind'; lineIndex: number; variableName: string };

function scanTopLevelVarAssignmentEvents(lines: string[]): TopLevelVarAssignmentEvent[] {
  const events: TopLevelVarAssignmentEvent[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!isTopLevelCodeLine(line)) continue;
    const constructorMatch = line.match(VAR_ASSIGN_RE);
    if (constructorMatch?.[1] && constructorMatch?.[2]) {
      events.push({
        kind: 'constructor',
        lineIndex: i,
        variableName: constructorMatch[1],
        className: constructorMatch[2],
      });
      continue;
    }
    const assignMatch = line.match(TOP_LEVEL_VAR_ASSIGN_RE);
    if (assignMatch?.[1]) {
      events.push({ kind: 'rebind', lineIndex: i, variableName: assignMatch[1] });
    }
  }
  return events;
}

function readTopLevelConstructorAssignments(lines: string[]): TopLevelVarAssignment[] {
  return scanTopLevelVarAssignmentEvents(lines)
    .filter((event): event is Extract<TopLevelVarAssignmentEvent, { kind: 'constructor' }> => event.kind === 'constructor')
    .map(({ lineIndex, variableName, className }) => ({ lineIndex, variableName, className }));
}

/** Top-level `variableName = ClassName(...)` declarations in source order. */
export function collectTopLevelVarAssignments(lines: string[], opts?: FindVarAssignmentLineIndexOpts): TopLevelVarAssignment[] {
  return readTopLevelConstructorAssignments(lines).filter(
    (assignment) => !opts?.allowedClassNames || opts.allowedClassNames.has(assignment.className)
  );
}

/** Latest top-level declaration per variable name (last occurrence wins). */
export function collectLastTopLevelVarAssignmentsByName(lines: string[], opts?: FindVarAssignmentLineIndexOpts): TopLevelVarAssignment[] {
  const effective = new Map<string, TopLevelVarAssignment>();
  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (event.kind === 'rebind') {
      effective.delete(event.variableName);
      continue;
    }
    if (opts?.allowedClassNames && !opts.allowedClassNames.has(event.className)) {
      effective.delete(event.variableName);
      continue;
    }
    effective.set(event.variableName, {
      lineIndex: event.lineIndex,
      variableName: event.variableName,
      className: event.className,
    });
  }
  return Array.from(effective.values());
}

/** 0-based line index of a top-level `variableName = ClassName(...)`, or null. */
export function findVarAssignmentLineIndex(lines: string[], variableName: string, opts?: FindVarAssignmentLineIndexOpts): number | null {
  const selection = opts?.selection ?? 'last';
  const disallowedConstructor = opts?.disallowedConstructor ?? 'clear';
  let firstIndex: number | null = null;
  let lastIndex: number | null = null;

  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (event.variableName !== variableName) continue;
    if (event.kind === 'rebind') {
      firstIndex = null;
      lastIndex = null;
      continue;
    }
    const classAllowed = !opts?.allowedClassNames || opts.allowedClassNames.has(event.className);
    if (!classAllowed) {
      if (disallowedConstructor === 'clear') {
        firstIndex = null;
        lastIndex = null;
      }
      continue;
    }
    if (firstIndex === null) firstIndex = event.lineIndex;
    lastIndex = event.lineIndex;
  }

  return selection === 'first' ? firstIndex : lastIndex;
}

export type ResolveConstructorDeclarationLineOpts = {
  constructorClassName?: string;
  fallbackLineIndex0Based?: number;
};

function matchesConstructorDeclarationLine(lines: string[], lineIndex: number, variableName: string, constructorClassName?: string): boolean {
  const match = (lines[lineIndex] ?? '').match(VAR_ASSIGN_RE);
  if (match?.[1] !== variableName) return false;
  if (constructorClassName && match?.[2] !== constructorClassName) return false;
  return true;
}

/** Resolves the constructor declaration line for device edits, preferring the card's class when provided. */
export function resolveConstructorDeclarationLineIndex(
  lines: string[],
  variableName: string,
  opts?: ResolveConstructorDeclarationLineOpts
): number | null {
  const { constructorClassName, fallbackLineIndex0Based } = opts ?? {};
  const lookupOpts = constructorClassName
    ? { allowedClassNames: new Set([constructorClassName]), disallowedConstructor: 'skip' as const }
    : undefined;

  const declLine = findVarAssignmentLineIndex(lines, variableName, lookupOpts);

  if (declLine === null && fallbackLineIndex0Based !== undefined && Number.isFinite(fallbackLineIndex0Based)) {
    if (matchesConstructorDeclarationLine(lines, fallbackLineIndex0Based, variableName, constructorClassName)) {
      return fallbackLineIndex0Based;
    }
  }

  return declLine;
}

type TextModelLike = {
  getValue?: () => string;
  getLineCount: () => number;
  getLineContent: (lineNumber: number) => string;
};

export function getTextModelLines(model: TextModelLike): string[] {
  if (typeof model.getValue === 'function') return splitCodeLines(model.getValue());
  const lines: string[] = [];
  for (let i = 1; i <= model.getLineCount(); i++) {
    lines.push(model.getLineContent(i));
  }
  return lines;
}

/** 1-based line after the last matching top-level constructor declaration, or null. */
export function findTopLevelVarDeclarationEndLine(model: TextModelLike, variableName: string, opts?: FindVarAssignmentLineIndexOpts): number | null {
  const lines = getTextModelLines(model);
  const declLine = findVarAssignmentLineIndex(lines, variableName, opts);
  if (declLine === null) return null;
  return findConstructorCallEndLineIndex(lines, declLine) + 1;
}

export type TopLevelVarDeclarationAnchor = {
  variableName: string;
  allowedClassNames?: ReadonlySet<string>;
};

export function findTopLevelVarDeclarationEndLineForAnchor(model: TextModelLike, anchor: TopLevelVarDeclarationAnchor): number | null {
  return findTopLevelVarDeclarationEndLine(model, anchor.variableName, {
    allowedClassNames: anchor.allowedClassNames,
    selection: 'first',
    disallowedConstructor: 'skip',
  });
}

/** Bump `insertLine` past declared action-target constructors referenced by `anchors`. */
export function bumpInsertLineForTopLevelVarDeclarationAnchors(
  model: TextModelLike,
  insertLine: number,
  anchors: ReadonlyArray<TopLevelVarDeclarationAnchor>
): number {
  let line = insertLine;
  for (const anchor of anchors) {
    const targetEndLine = findTopLevelVarDeclarationEndLineForAnchor(model, anchor);
    if (targetEndLine === null) continue;
    line = Math.max(line, targetEndLine + 1);
  }
  return line;
}

/** 0-based end line of a constructor call starting at `startLineIndex` (inclusive). */
export function findConstructorCallEndLineIndex(lines: readonly string[], startLineIndex: number): number {
  let depth = 0;
  const stringState = createPythonStringScanState();
  for (let lineNum = startLineIndex; lineNum < lines.length; lineNum++) {
    const line = lines[lineNum] ?? '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      const stringChars = scanPythonStringLiteralAt(stringState, line, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (ch === '#') break;

      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) return lineNum;
      }
    }
  }
  return startLineIndex;
}

/** 1-based end line of a constructor call starting at `startLineOneBased` (inclusive). */
export function findConstructorCallEndLine(model: TextModelLike, startLineOneBased: number): number {
  const lineCount = model.getLineCount();
  if (!Number.isFinite(startLineOneBased) || startLineOneBased < 1 || startLineOneBased > lineCount) {
    return startLineOneBased;
  }
  const lines = getTextModelLines(model);
  return findConstructorCallEndLineIndex(lines, startLineOneBased - 1) + 1;
}

function findConstructorKwargValueEnd(
  lines: string[],
  startLineIndex: number,
  valueStartCol: number,
  endLineIndex: number
): { lineIndex: number; col: number } {
  let depth = 0;
  const stringState = createPythonStringScanState();

  for (let lineNum = startLineIndex; lineNum <= endLineIndex; lineNum++) {
    const line = lines[lineNum] ?? '';
    const startCol = lineNum === startLineIndex ? Math.max(valueStartCol, 0) : 0;
    for (let i = startCol; i < line.length; i++) {
      const ch = line[i];
      const stringChars = scanPythonStringLiteralAt(stringState, line, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (ch === '(' || ch === '[' || ch === '{') {
        depth++;
        continue;
      }
      if (ch === ')' || ch === ']' || ch === '}') {
        if (depth === 0) return { lineIndex: lineNum, col: i };
        depth--;
        continue;
      }
      if (ch === ',' && depth === 0) return { lineIndex: lineNum, col: i };
    }
  }

  const finalLine = Math.min(endLineIndex, lines.length - 1);
  const finalLineContent = lines[finalLine] ?? '';
  return { lineIndex: finalLine, col: finalLineContent.length };
}

export interface ConstructorKwargSpan {
  startOffset: number;
  endOffset: number;
}

/** Byte span covering `kwargName=…` inside the constructor at `declarationLineIndex`. */
export function findConstructorKwargSpan(code: string, declarationLineIndex: number, kwargName: string): ConstructorKwargSpan | null {
  const lines = splitCodeLines(code);
  const endLineIndex = findConstructorCallEndLineIndex(lines, declarationLineIndex);

  for (let lineNum = declarationLineIndex; lineNum <= endLineIndex; lineNum++) {
    const line = lines[lineNum] ?? '';
    let searchIndex = 0;
    while (searchIndex < line.length) {
      const parameterArg = line.indexOf(kwargName, searchIndex);
      if (parameterArg < 0) break;

      const before = parameterArg > 0 ? line[parameterArg - 1] : '';
      const after = parameterArg + kwargName.length < line.length ? line[parameterArg + kwargName.length] : '';
      if (/[A-Za-z0-9_]/.test(before) || /[A-Za-z0-9_]/.test(after)) {
        searchIndex = parameterArg + kwargName.length;
        continue;
      }

      const equals = line.indexOf('=', parameterArg + kwargName.length);
      if (equals < 0) {
        searchIndex = parameterArg + kwargName.length;
        continue;
      }

      const endPos = findConstructorKwargValueEnd(lines, lineNum, equals + 1, endLineIndex);
      return {
        startOffset: offsetAtLineColumn(lines, lineNum, parameterArg),
        endOffset: offsetAtLineColumn(lines, endPos.lineIndex, endPos.col),
      };
    }
  }

  return null;
}

export function readConstructorKeywordValue(code: string | undefined, declarationLineIndex: number, keywordName: string): string | null | undefined {
  if (!code) return undefined;
  const lines = splitCodeLines(code);
  const line = lines[declarationLineIndex] ?? '';
  const openParenIndex = line.indexOf('(');
  if (openParenIndex < 0) return null;

  const openOffset = offsetAtCodeLineColumn(code, declarationLineIndex, openParenIndex);
  const span = readBalancedDelimiterSpan(code, openOffset, '(', ')', { ignoreLineComments: true });
  if (!span) return null;

  for (const token of splitTopLevelByComma(span.inner)) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(token);
    if (assignmentIndex <= 0) continue;
    const key = token.slice(0, assignmentIndex).trim();
    if (key !== keywordName) continue;
    return token.slice(assignmentIndex + 1).trim();
  }

  return undefined;
}

function escapePythonScanRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function readPropertyAssignmentValue(
  code: string | undefined,
  targetName: string,
  propertyName: string,
  startLineIndex: number,
  endLineIndex = Number.POSITIVE_INFINITY
): string | undefined {
  if (!code || !targetName || !propertyName) return undefined;
  const lines = splitCodeLines(code);
  const assignmentRe = new RegExp(`^\\s*${escapePythonScanRegExp(targetName)}\\.${escapePythonScanRegExp(propertyName)}\\s*=`);
  const declarationIndent = pythonLineIndentColumn(lines[startLineIndex] ?? '');
  let value: string | undefined;

  for (let lineIndex = Math.max(0, startLineIndex + 1); lineIndex < Math.min(lines.length, endLineIndex); lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (pythonLineIndentColumn(line) > declarationIndent) continue;
    if (!assignmentRe.test(line)) continue;
    const targetOffset = offsetAtCodeLineColumn(code, lineIndex, line.indexOf(targetName));
    if (isOffsetInPythonStringOrLineComment(code, targetOffset)) continue;
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(line);
    if (assignmentIndex <= 0) continue;
    const rawValue = stripTrailingPythonLineComment(line.slice(assignmentIndex + 1));
    if (rawValue) value = rawValue;
  }

  return value;
}

/** Replaces one constructor keyword argument on the given declaration line. */
export function replaceConstructorKwargInCode(code: string, declarationLineIndex: number, kwargName: string, newValue: string): string {
  const span = findConstructorKwargSpan(code, declarationLineIndex, kwargName);
  if (!span) return code;
  return code.slice(0, span.startOffset) + `${kwargName}=${newValue}` + code.slice(span.endOffset);
}

/**
 * Replaces a constructor kwarg on `parentName = ClassName(...)`, resolving the declaration by variable name
 * (stored card line numbers can be stale when code is inserted above the device).
 */
export function replaceConstructorKwargInProgramCode(
  code: string,
  parentName: string,
  kwargName: string,
  newValue: string,
  fallbackLineIndex0Based?: number,
  constructorClassName?: string
): string {
  const lines = splitCodeLines(code);
  const declLine = resolveConstructorDeclarationLineIndex(lines, parentName, {
    constructorClassName,
    fallbackLineIndex0Based,
  });
  if (declLine === null) return code;

  return replaceConstructorKwargInCode(code, declLine, kwargName, newValue);
}

const REGISTER_CALLABLE_PARAMETER_KWARG = 'parameter';
const REGISTER_CALLABLE_DATATYPE_KWARG = 'datatype';
const REGISTER_CALLABLE_GETTER_KWARG = 'getter';

export interface RegisterCallableRegistration {
  /** Stream parameter name passed to `register_callable` (used for `DataStreamStore` requests and widget labels). */
  parameter: string;
}

function parseStringLiteralCallArgValue(rawArg: string): string | null {
  const trimmed = rawArg.trim();
  if (!isPlainPythonQuotedStringLiteral(trimmed) || trimmed.length < 2) return null;
  return parsePythonPlainStringLiteralValue(trimmed);
}

function hasNonEmptyRegisterCallableGetterArg(rawArg: string | undefined): boolean {
  return Boolean(rawArg?.trim());
}

/** Positional getter arg for `register_callable(parameter, datatype, getter)` only. */
function getRegisterCallablePositionalGetterArg(args: string[]): string | undefined {
  const firstArg = args[0]?.trim() ?? '';
  if (!isPlainPythonQuotedStringLiteral(firstArg)) return undefined;

  const secondArg = args[1]?.trim() ?? '';
  if (!isPlainPythonQuotedStringLiteral(secondArg)) return undefined;

  return args[2];
}

/** True when a `datatype=` kwarg with a string literal value is present. */
function extractRegisterCallableHasDatatypeFromKeywordArgs(args: string[]): boolean {
  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_DATATYPE_KWARG) continue;
    return parseStringLiteralCallArgValue(entry.slice(assignmentIndex + 1)) !== null;
  }

  return false;
}

/** True when a datatype argument is present (positional second arg or `datatype=` kwarg). */
function extractRegisterCallableHasDatatypeArg(argsText: string): boolean {
  const args = splitTopLevelByComma(argsText);
  const firstArg = args[0]?.trim() ?? '';
  if (isPlainPythonQuotedStringLiteral(firstArg) && isPlainPythonQuotedStringLiteral(args[1]?.trim() ?? '')) {
    return true;
  }

  return extractRegisterCallableHasDatatypeFromKeywordArgs(args);
}

/** Stream key from positional first arg or `parameter="..."` kwargs on `register_callable`. */
function extractRegisterCallableParameterFromArgs(argsText: string): string | null {
  const args = splitTopLevelByComma(argsText);
  const fromPositional = parseStringLiteralCallArgValue(args[0] ?? '');
  if (fromPositional !== null) return fromPositional;

  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_PARAMETER_KWARG) continue;
    return parseStringLiteralCallArgValue(entry.slice(assignmentIndex + 1));
  }

  return null;
}

/** True when a getter argument is present (positional or `getter=`), regardless of expression shape. */
function extractRegisterCallableHasGetterArg(argsText: string): boolean {
  const args = splitTopLevelByComma(argsText);
  const positionalGetter = getRegisterCallablePositionalGetterArg(args);
  if (positionalGetter !== undefined) {
    return hasNonEmptyRegisterCallableGetterArg(positionalGetter);
  }

  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_GETTER_KWARG) continue;
    return hasNonEmptyRegisterCallableGetterArg(entry.slice(assignmentIndex + 1));
  }

  return false;
}

function extractRegisterCallableRegistrationFromArgs(argsText: string): RegisterCallableRegistration | null {
  const parameter = extractRegisterCallableParameterFromArgs(argsText);
  if (parameter === null || !extractRegisterCallableHasDatatypeArg(argsText) || !extractRegisterCallableHasGetterArg(argsText)) {
    return null;
  }
  return { parameter };
}

/**
 * Scans Python code for `DataStreamStore.get_instance().register_callable(...)` calls
 * and returns stream parameters for each valid registration.
 */
export function parseRegisterCallableRegistrations(code: string): RegisterCallableRegistration[] {
  const seen = new Set<string>();
  const registrations: RegisterCallableRegistration[] = [];
  const re = /DataStreamStore\.get_instance\(\)\.register_callable\s*\(/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(code)) !== null) {
    const openParenIndex = match.index + match[0].length - 1;
    const span = readBalancedDelimiterSpan(code, openParenIndex, '(', ')');
    if (!span) continue;

    const registration = extractRegisterCallableRegistrationFromArgs(span.inner);
    if (registration !== null && !seen.has(registration.parameter)) {
      seen.add(registration.parameter);
      registrations.push(registration);
    }
  }

  return registrations;
}

/** Stream parameters registered via `register_callable` in project code. */
export function buildRegisteredCustomCallableParametersFromCode(code: string): ReadonlySet<string> {
  return new Set(parseRegisterCallableRegistrations(code).map((registration) => registration.parameter));
}

/**
 * Scans Python code for `DataStreamStore.get_instance().register_callable(...)` calls
 * and returns stream parameter names (for `DataStreamStore` requests and widget `dataKey`).
 */
export function parseRegisterCallableNames(code: string): string[] {
  return parseRegisterCallableRegistrations(code).map((registration) => registration.parameter);
}
